import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { Plus, Search, Edit, Eye, X, Save, User, Mail, Phone, Calendar, DollarSign, Building, Upload, Download } from 'lucide-react';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(false);
  const [bulkUploadStage, setBulkUploadStage] = useState('');
  const [bulkUploadPercent, setBulkUploadPercent] = useState(0);
  const [newEmployee, setNewEmployee] = useState({
    emp_code: '',
    full_name: '',
    email: '',
    phone: '',
    department_id: '',
    designation_id: '',
    date_of_joining: '',
    date_of_birth: '',
    address: '',
    base_salary: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        apiClient.get('/api/employees'),
        apiClient.get('/api/departments')
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/employees', newEmployee);
      fetchData();
      setShowAddModal(false);
      setNewEmployee({
        emp_code: '',
        full_name: '',
        email: '',
        phone: '',
        department_id: '',
        designation_id: '',
        date_of_joining: '',
        date_of_birth: '',
        address: '',
        base_salary: ''
      });
    } catch (error) {
      console.error('Error adding employee:', error);
      alert('Error adding employee: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleViewEmployee = (emp) => {
    setSelectedEmployee(emp);
    setShowViewModal(true);
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee({...emp});
    setShowEditModal(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        full_name: editingEmployee.full_name,
        phone: editingEmployee.phone,
        department_id: editingEmployee.department_id,
        designation_id: editingEmployee.designation_id,
        base_salary: parseFloat(editingEmployee.base_salary),
        address: editingEmployee.address
      };
      
      await apiClient.put(`/api/employees/${editingEmployee.id}`, updateData);
      fetchData();
      setShowEditModal(false);
      setEditingEmployee(null);
    } catch (error) {
      console.error('Error updating employee:', error);
      alert('Error updating employee: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleBulkUploadFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setBulkUploadFile(file);
    }
  };

  const handleBulkEmployeeUpload = async () => {
    if (!bulkUploadFile) {
      alert('Please select a file first');
      return;
    }

    setBulkUploadProgress(true);
    setBulkUploadStage('Uploading employee directory...');
    setBulkUploadPercent(10);
    
    const formData = new FormData();
    formData.append('file', bulkUploadFile);

    try {
      setBulkUploadPercent(30);
      setBulkUploadStage('Processing employee records...');
      
      const response = await apiClient.post('/api/employees/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 30) / progressEvent.total) + 30;
          setBulkUploadPercent(percentCompleted);
        }
      });
      
      setBulkUploadPercent(80);
      setBulkUploadStage('Creating employee profiles...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setBulkUploadPercent(100);
      setBulkUploadStage('Completed!');
      
      alert(`Success! ${response.data.inserted} employees added, ${response.data.updated} employees updated.`);
      fetchData();
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
    } catch (error) {
      console.error('Error uploading employees:', error);
      alert('Error uploading employees: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setBulkUploadProgress(false);
      setBulkUploadPercent(0);
      setBulkUploadStage('');
    }
  };

  const handleDownloadEmployeeTemplate = async () => {
    try {
      const response = await apiClient.get('/api/employees/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template. Please try again.');
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : deptId;
  };

  const filteredEmployees = employees.filter(emp =>
    emp.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.emp_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-[hsl(210_15%_92%)]'>Employees</h1>
          <p className='text-[hsl(215_10%_70%)] mt-1'>Manage your workforce - {employees.length} employees</p>
        </div>
        <div className='flex gap-3'>
          <button
            onClick={() => setShowBulkUploadModal(true)}
            data-testid='bulk-upload-employees-button'
            className='flex items-center gap-2 bg-[hsl(215_8%_20%)] text-[hsl(210_15%_92%)] border border-[hsl(215_10%_22%)] px-6 py-3 rounded-lg font-semibold hover:bg-[hsl(215_8%_24%)] hover:border-[#00B8B8]/50 transition-colors duration-150'
          >
            <Upload size={20} />
            Bulk Upload
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid='add-employee-button'
            className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-colors duration-150'
          >
            <Plus size={20} />
            Add Employee
          </button>
        </div>
      </div>

      {/* Search */}
      <div className='mb-6'>
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(215_10%_50%)]' size={20} />
          <input
            type='text'
            placeholder='Search employees by name, code, or email...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid='employee-search-input'
            className='w-full pl-10 pr-4 py-3 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] placeholder-[hsl(215_10%_50%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
          />
        </div>
      </div>

      {/* Employees Table */}
      <div className='table-container'>
        <table className='w-full'>
          <thead className='table-header'>
            <tr>
              <th className='text-left p-4'>Emp Code</th>
              <th className='text-left p-4'>Name</th>
              <th className='text-left p-4'>Email</th>
              <th className='text-left p-4'>Phone</th>
              <th className='text-left p-4'>Department</th>
              <th className='text-left p-4'>Base Salary</th>
              <th className='text-left p-4'>Join Date</th>
              <th className='text-left p-4'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan='8' className='text-center p-8 text-[hsl(215_10%_70%)]'>
                  No employees found
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => (
                <tr key={emp.id} className='border-t border-[hsl(215_10%_22%)] hover:bg-[hsl(215_8%_20%)/50] transition-colors duration-150'>
                  <td className='p-4 font-mono font-medium text-[hsl(210_15%_92%)]'>{emp.emp_code}</td>
                  <td className='p-4 font-medium text-[hsl(210_15%_92%)]'>{emp.full_name}</td>
                  <td className='p-4 text-[hsl(215_10%_70%)]'>{emp.email}</td>
                  <td className='p-4 text-[hsl(215_10%_70%)]'>{emp.phone || 'N/A'}</td>
                  <td className='p-4 text-[hsl(215_10%_70%)]'>{getDepartmentName(emp.department_id)}</td>
                  <td className='p-4 font-mono font-medium text-[hsl(210_15%_92%)]'>₹{Number(emp.base_salary).toLocaleString()}</td>
                  <td className='p-4 text-[hsl(215_10%_70%)]'>{emp.date_of_joining}</td>
                  <td className='p-4'>
                    <div className='flex items-center gap-2'>
                      <button
                        onClick={() => handleViewEmployee(emp)}
                        data-testid={`view-employee-${emp.emp_code}`}
                        className='p-2 text-[#00B8B8] hover:bg-[rgba(0,184,184,0.1)] rounded transition-colors duration-150'
                        title='View Details'
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEditEmployee(emp)}
                        data-testid={`edit-employee-${emp.emp_code}`}
                        className='p-2 text-[#0EA5E9] hover:bg-[rgba(14,165,233,0.1)] rounded transition-colors duration-150'
                        title='Edit Employee'
                      >
                        <Edit size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Employee Modal */}
      {showViewModal && selectedEmployee && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowViewModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)]'>Employee Details</h2>
              <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-[hsl(215_8%_20%)] rounded transition-colors duration-150'>
                <X size={24} className='text-[hsl(215_10%_70%)]' />
              </button>
            </div>
            
            <div className='space-y-6'>
              {/* Personal Information */}
              <div>
                <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)] mb-4 flex items-center gap-2'>
                  <User size={20} className='text-[#00B8B8]' />
                  Personal Information
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Employee Code</p>
                    <p className='font-mono font-semibold text-[hsl(210_15%_92%)]'>{selectedEmployee.emp_code}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Full Name</p>
                    <p className='font-semibold text-[hsl(210_15%_92%)]'>{selectedEmployee.full_name}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Email</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.email}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Phone</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.phone || 'N/A'}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Date of Birth</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.date_of_birth || 'N/A'}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Address</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div>
                <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)] mb-4 flex items-center gap-2'>
                  <Building size={20} className='text-[#00B8B8]' />
                  Employment Information
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Department</p>
                    <p className='font-semibold text-[hsl(210_15%_92%)]'>{getDepartmentName(selectedEmployee.department_id)}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Designation</p>
                    <p className='font-semibold text-[hsl(210_15%_92%)]'>{selectedEmployee.designation_id}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Date of Joining</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.date_of_joining}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Bank Name</p>
                    <p className='text-[hsl(210_15%_92%)]'>{selectedEmployee.bank_name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Salary Information */}
              <div>
                <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)] mb-4 flex items-center gap-2'>
                  <DollarSign size={20} className='text-[#00B8B8]' />
                  Salary Information
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Salary (CTC)</p>
                    <p className='font-mono font-bold text-[#00B8B8] text-lg'>₹{Number(selectedEmployee.salary_ctc || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Gross Salary</p>
                    <p className='font-mono font-bold text-[#10B981] text-lg'>₹{Number(selectedEmployee.base_salary || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Basic (40%)</p>
                    <p className='font-mono text-[hsl(210_15%_92%)]'>₹{Number(selectedEmployee.basic || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>HRA (40%)</p>
                    <p className='font-mono text-[hsl(210_15%_92%)]'>₹{Number(selectedEmployee.hra || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Conveyance (10%)</p>
                    <p className='font-mono text-[hsl(210_15%_92%)]'>₹{Number(selectedEmployee.conveyance || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)]'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Medical (10%)</p>
                    <p className='font-mono text-[hsl(210_15%_92%)]'>₹{Number(selectedEmployee.medical_allowance || 0).toLocaleString()}</p>
                  </div>
                  <div className='bg-[hsl(215_8%_20%)] p-4 rounded-lg border border-[hsl(215_10%_22%)] col-span-2'>
                    <p className='text-xs text-[hsl(215_10%_70%)] mb-1'>Special Allowance</p>
                    <p className='font-mono text-[hsl(210_15%_92%)]'>₹{Number(selectedEmployee.special_allowance || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditEmployee(selectedEmployee);
                  }}
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] flex items-center gap-2 transition-colors duration-150'
                >
                  <Edit size={18} />
                  Edit Employee
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] transition-colors duration-150'
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && editingEmployee && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowEditModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)]'>Edit Employee</h2>
              <button onClick={() => setShowEditModal(false)} className='p-2 hover:bg-[hsl(215_8%_20%)] rounded transition-colors duration-150'>
                <X size={24} className='text-[hsl(215_10%_70%)]' />
              </button>
            </div>
            
            <form onSubmit={handleUpdateEmployee} className='space-y-6'>
              {/* Personal Info */}
              <div>
                <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)] mb-4 pb-2 border-b border-[hsl(215_10%_22%)]'>Personal Information</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Employee Code</label>
                    <input
                      type='text'
                      value={editingEmployee.emp_code}
                      disabled
                      className='w-full px-4 py-2 bg-[hsl(215_8%_20%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(215_10%_70%)] cursor-not-allowed'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Full Name*</label>
                    <input
                      type='text'
                      required
                      value={editingEmployee.full_name}
                      onChange={(e) => setEditingEmployee({...editingEmployee, full_name: e.target.value})}
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Email</label>
                    <input
                      type='email'
                      value={editingEmployee.email}
                      disabled
                      className='w-full px-4 py-2 bg-[hsl(215_8%_20%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(215_10%_70%)] cursor-not-allowed'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Phone</label>
                    <input
                      type='text'
                      value={editingEmployee.phone || ''}
                      onChange={(e) => setEditingEmployee({...editingEmployee, phone: e.target.value})}
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    />
                  </div>
                  <div className='col-span-2'>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Address</label>
                    <textarea
                      value={editingEmployee.address || ''}
                      onChange={(e) => setEditingEmployee({...editingEmployee, address: e.target.value})}
                      rows='2'
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Employment Info */}
              <div>
                <h3 className='text-lg font-semibold text-[hsl(210_15%_92%)] mb-4 pb-2 border-b border-[hsl(215_10%_22%)]'>Employment Information</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Department</label>
                    <select
                      value={editingEmployee.department_id}
                      onChange={(e) => setEditingEmployee({...editingEmployee, department_id: e.target.value})}
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    >
                      <option value=''>Select Department</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Designation</label>
                    <input
                      type='text'
                      value={editingEmployee.designation_id}
                      onChange={(e) => setEditingEmployee({...editingEmployee, designation_id: e.target.value})}
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Base Salary*</label>
                    <input
                      type='number'
                      required
                      value={editingEmployee.base_salary}
                      onChange={(e) => setEditingEmployee({...editingEmployee, base_salary: e.target.value})}
                      className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Date of Joining</label>
                    <input
                      type='date'
                      value={editingEmployee.date_of_joining}
                      disabled
                      className='w-full px-4 py-2 bg-[hsl(215_8%_20%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(215_10%_70%)] cursor-not-allowed'
                    />
                  </div>
                </div>
              </div>

              <div className='flex gap-4 justify-end pt-4 border-t border-[hsl(215_10%_22%)]'>
                <button
                  type='button'
                  onClick={() => setShowEditModal(false)}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] transition-colors duration-150'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  data-testid='save-employee-changes'
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] flex items-center gap-2 transition-colors duration-150'
                >
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowAddModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)] mb-6'>Add New Employee</h2>
            <form onSubmit={handleAddEmployee} className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Employee Code*</label>
                  <input
                    type='text'
                    required
                    value={newEmployee.emp_code}
                    onChange={(e) => setNewEmployee({...newEmployee, emp_code: e.target.value})}
                    data-testid='new-emp-code-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Full Name*</label>
                  <input
                    type='text'
                    required
                    value={newEmployee.full_name}
                    onChange={(e) => setNewEmployee({...newEmployee, full_name: e.target.value})}
                    data-testid='new-emp-name-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Email*</label>
                  <input
                    type='email'
                    required
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                    data-testid='new-emp-email-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Phone</label>
                  <input
                    type='text'
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                    data-testid='new-emp-phone-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Department*</label>
                  <select
                    required
                    value={newEmployee.department_id}
                    onChange={(e) => setNewEmployee({...newEmployee, department_id: e.target.value})}
                    data-testid='new-emp-department-select'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  >
                    <option value=''>Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Designation*</label>
                  <input
                    type='text'
                    required
                    value={newEmployee.designation_id}
                    onChange={(e) => setNewEmployee({...newEmployee, designation_id: e.target.value})}
                    data-testid='new-emp-designation-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Date of Joining*</label>
                  <input
                    type='date'
                    required
                    value={newEmployee.date_of_joining}
                    onChange={(e) => setNewEmployee({...newEmployee, date_of_joining: e.target.value})}
                    data-testid='new-emp-doj-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Base Salary*</label>
                  <input
                    type='number'
                    required
                    value={newEmployee.base_salary}
                    onChange={(e) => setNewEmployee({...newEmployee, base_salary: e.target.value})}
                    data-testid='new-emp-salary-input'
                    className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20'
                  />
                </div>
              </div>
              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => setShowAddModal(false)}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] transition-colors duration-150'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  data-testid='submit-new-employee'
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] transition-colors duration-150'
                >
                  Add Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className='fixed inset-0 bg-black/75 flex items-center justify-center z-50' onClick={() => setShowBulkUploadModal(false)}>
          <div className='bg-[hsl(220_6%_8%)] border border-[hsl(215_10%_22%)] rounded-xl p-6 max-w-2xl w-full m-4 shadow-[0_12px_40px_rgba(0,0,0,0.50)]' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[hsl(210_15%_92%)] mb-6'>Bulk Upload Employees</h2>
            
            <div className='bg-[rgba(14,165,233,0.1)] border border-[rgba(14,165,233,0.3)] rounded-lg p-4 mb-6'>
              <h4 className='font-semibold text-[#0EA5E9] mb-2'>Employee Directory Upload</h4>
              <p className='text-sm text-[hsl(215_10%_70%)] mb-2'>Upload your Employee Directory Excel file to add or update multiple employees at once.</p>
              <p className='text-sm text-[hsl(215_10%_70%)]'>Required columns: Employee No, Employee Name, Join Date, Status, Phone No, Email</p>
            </div>

            <div className='space-y-4'>
              <div>
                <p className='text-sm text-[hsl(215_10%_70%)] mb-4'>Upload Excel file with employee data. Download the template to see the required format.</p>
                <button
                  onClick={handleDownloadEmployeeTemplate}
                  data-testid='download-employee-template'
                  className='flex items-center gap-2 px-4 py-2 border border-[#00B8B8] text-[#00B8B8] rounded-lg hover:bg-[rgba(0,184,184,0.1)] mb-4 transition-colors duration-150'
                >
                  <Download size={18} />
                  Download Template (Excel)
                </button>
              </div>
              
              <div>
                <label className='block text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>Select Employee Directory File</label>
                <input
                  type='file'
                  accept='.xlsx,.xls'
                  onChange={handleBulkUploadFileSelect}
                  data-testid='employee-bulk-file-input'
                  className='w-full px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)]'
                />
                {bulkUploadFile && (
                  <div className='mt-3 p-3 bg-[#073B3B] border border-[#0E5F5F] rounded-lg'>
                    <p className='text-sm text-[#4DD9D9]'>
                      <strong>Selected:</strong> {bulkUploadFile.name} ({(bulkUploadFile.size / 1024).toFixed(2)} KB)
                    </p>
                  </div>
                )}
              </div>

              <div className='bg-[hsl(215_8%_20%)] rounded-lg p-4 border border-[hsl(215_10%_22%)]'>
                <p className='text-sm font-medium text-[hsl(210_15%_92%)] mb-2'>What happens during upload:</p>
                <ul className='text-sm text-[hsl(215_10%_70%)] list-disc list-inside space-y-1'>
                  <li>Existing employees (matching Employee No) will be updated</li>
                  <li>New employees will be added to the system</li>
                  <li>Default department and designation assigned to new employees</li>
                  <li>Leave balances created automatically (12 CL, 12 SL, 15 EL)</li>
                  <li>You can update salaries later via Bulk Salary Upload</li>
                </ul>
              </div>

              <div className='flex gap-4 justify-end mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setBulkUploadFile(null);
                  }}
                  disabled={bulkUploadProgress}
                  className='px-6 py-2 border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] hover:bg-[hsl(215_8%_20%)] disabled:opacity-50 transition-colors duration-150'
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkEmployeeUpload}
                  disabled={!bulkUploadFile || bulkUploadProgress}
                  data-testid='submit-bulk-employee-upload'
                  className='px-6 py-2 bg-[#00B8B8] text-white rounded-lg hover:bg-[#0ED2D2] disabled:opacity-50 transition-colors duration-150'
                >
                  {bulkUploadProgress ? 'Uploading...' : 'Upload Employees'}
                </button>
              </div>

              {bulkUploadProgress && (
                <div className='mt-6 p-4 bg-[rgba(14,165,233,0.1)] border border-[rgba(14,165,233,0.3)] rounded-lg'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-[#0EA5E9]'>{bulkUploadStage}</span>
                    <span className='text-sm font-bold text-[#0EA5E9]'>{bulkUploadPercent}%</span>
                  </div>
                  <div className='w-full bg-[hsl(215_10%_22%)] rounded-full h-3'>
                    <div
                      className='bg-[#00B8B8] h-3 rounded-full transition-all duration-500'
                      style={{ width: `${bulkUploadPercent}%` }}
                    ></div>
                  </div>
                  <p className='text-xs text-[hsl(215_10%_70%)] mt-2'>Processing employee directory...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Employees;